import React, { useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { X } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY, DETAIL_PANEL_WIDTH } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useStructuresStore } from '../../store/structuresStore';
import { useEscapeToClose } from '../../utils/escapeStack';
import { useLeavingPanel } from '../../hooks/useLeavingPanel';
import { DockedPanel } from '../layout/DockedPanel';
import { PostComposer } from '../post-create/PostComposer';
import type { PostSubmitOutcome } from '../../hooks/usePostSubmit';
import { DESKTOP_COMPOSER_DOCK_ENABLED } from '../../constants/composer';

const HEADING_ID = 'composer-panel-heading';

/**
 * Desktop map route host for the observation composer (panel-system.md
 * Phase 3A): a right-docked panel (testID `composer-panel`) instead of
 * routing to /post/create. CreateActionMenuItems' "New post" opens this
 * instead when DESKTOP_COMPOSER_DOCK_ENABLED is on and the desktop shell is
 * on the map route; the flag off (or any other entry point) still routes.
 * Mounted by app/(tabs)/index.tsx on the desktop shell; self-gates on the
 * flag and on uiStore.composerDockOpen, same convention as
 * PropertyDockPanel.web.tsx.
 *
 * Location comes from a draggable map pin (useComposerPinLayer.web.ts)
 * instead of the location drill-in — see PostComposer's `dockedMapPin` prop.
 * Submit or Escape both close the dock (onDone/useEscapeToClose below);
 * neither navigates.
 *
 * Same right-hand dock surface as the structure/post/presentation panels
 * and the add-property dock (see app/(tabs)/index.tsx's
 * handleStructurePress/handlePostPress, Map.web.tsx's handleOpenSlideshow,
 * and PropertyDockPanel's own open effect): opening this clears those, and
 * opening any of those closes this in turn.
 */
export function ComposerDockPanel() {
  const open = useUIStore((s) => s.composerDockOpen);
  const close = useUIStore((s) => s.closeComposerDock);
  const composerPin = useUIStore((s) => s.composerPin);
  const enabled = DESKTOP_COMPOSER_DOCK_ENABLED && open;
  useEscapeToClose(enabled, close);

  // Another right-dock surface taking the slot in this same render replaces
  // the composer at once. closeComposerDock nulls composerPin, so the pin is
  // captured for the exit; the effect below stays keyed to `enabled`.
  const structureSelected = useStructuresStore((s) => s.selectedStructure != null);
  const postDocked = useUIStore((s) => s.dockedPostId != null);
  const sheetOpen = useUIStore((s) => s.activeSheet != null);
  const presenting = useUIStore((s) => s.presentationActive);
  const leave = useLeavingPanel(
    enabled,
    { pin: composerPin },
    { instant: structureSelected || postDocked || sheetOpen || presenting }
  );

  // Opening this dock clears the other right-hand surfaces — same
  // "opening one clears the others" pattern as PropertyDockPanel.
  useEffect(() => {
    if (!enabled) return;
    useStructuresStore.getState().selectStructure(null);
    useUIStore.getState().closeDockedPost();
    useUIStore.getState().closeSheet();
    useUIStore.getState().requestSlideshowClose();
  }, [enabled]);

  if (!leave.mounted) return null;

  const handleDone = (_result: PostSubmitOutcome) => close();

  return (
    <DockedPanel
      testID="composer-panel"
      width={DETAIL_PANEL_WIDTH}
      scroll={false}
      shown={leave.shown}
      onExited={leave.onExited}
      ariaLabelledBy={HEADING_ID}>
      <View style={styles.container}>
        <ComposerDockHeader onClose={close} />
        <View style={styles.body}>
          <PostComposer dockedMapPin={leave.lastContent?.pin ?? null} onDone={handleDone} />
        </View>
      </View>
    </DockedPanel>
  );
}

function ComposerDockHeader({ onClose }: { onClose: () => void }) {
  const { colors } = useTheme();
  return (
    <View style={[styles.header, { borderBottomColor: colors.border }]}>
      <Text nativeID={HEADING_ID} style={[styles.title, { color: colors.text }]}>
        New post
      </Text>
      <TouchableOpacity
        onPress={onClose}
        testID="composer-panel-close"
        accessibilityRole="button"
        accessibilityLabel="Close"
        aria-label="Close"
        hitSlop={8}>
        <X size={20} color={colors.textSecondary} />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  body: {
    flex: 1,
    minHeight: 0,
  },
});
